import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingColumns } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import AppButton from "../Button";
import FormSelect from "../FormSelect";
import BlurLoading from "../../BlurLoading";
import useNewBslAccountAPI from "../../../api/bslAccountAPI";
import useNewLoginAPI from "../../../api/loginAPI";
import { setFeatureData } from "../../../store/slices/appSlice";
import "../../../Styles/components/modal.scss";
import "../../../Styles/components/apply-for-iban-modal.scss";

function mapBranchOptions(branches) {
  return [
    {
      value: "",
      label: "Select a Branch",
    },
    ...((Array.isArray(branches) ? branches : []).map((branch) => ({
      value: String(branch?.ID ?? ""),
      label: branch?.Name || "-",
    })) || []),
  ];
}

function notifyIbanStatusUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("new:iban-status-updated", {
      detail: { timestamp: Date.now() },
    }),
  );
}

export default function ApplyForIbanModal({
  onApplied,
  onClose,
  onSubmitError,
  open,
}) {
  const dispatch = useDispatch();
  const { GetIbanBranches, GetBankAccountStatus, SubmitIbanApplication } =
    useNewBslAccountAPI();
  const { checkQrAxios } = useNewLoginAPI();
  const getIbanBranchesRef = useRef(GetIbanBranches);
  const getBankAccountStatusRef = useRef(GetBankAccountStatus);
  const submitIbanApplicationRef = useRef(SubmitIbanApplication);
  const checkQrAxiosRef = useRef(checkQrAxios);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  getIbanBranchesRef.current = GetIbanBranches;
  getBankAccountStatusRef.current = GetBankAccountStatus;
  submitIbanApplicationRef.current = SubmitIbanApplication;
  checkQrAxiosRef.current = checkQrAxios;

  const branchOptions = useMemo(() => mapBranchOptions(branches), [branches]);
  const verificationRef = sessionStorage.getItem("verificationRef");
  const resetState = useCallback(() => {
    setBranches([]);
    setSelectedBranchId("");
    setError("");
    setLoading(false);
    setSubmitting(false);
    dispatch(
      setFeatureData({
        section: "details",
        key: "ibanBranches",
        value: null,
      }),
    );
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const loadBranches = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await getIbanBranchesRef.current();
      setBranches(Array.isArray(payload?.Branches) ? payload.Branches : []);
    } catch (loadError) {
      setBranches([]);
      setError("Unable to load branches right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    loadBranches().catch(() => {});
  }, [open, resetState]);

  const handleSubmit = useCallback(async () => {
    if (!selectedBranchId) {
      setError("Please select a branch.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const payload = await submitIbanApplicationRef.current({
        branchID: selectedBranchId,
        verificationRef: verificationRef,
      });

      await checkQrAxiosRef.current().catch(() => {});
      await getBankAccountStatusRef.current().catch(() => {});
      notifyIbanStatusUpdated();
      onApplied?.(payload);
      handleClose();
    } catch (submitError) {
      setSelectedBranchId("");
      setError("");
      onSubmitError?.(submitError);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  }, [handleClose, onApplied, onSubmitError, selectedBranchId]);

  return (
    <>
      <Modal
        centered
        show={open}
        onHide={handleClose}
        dialogClassName="validation-popup-modal apply-for-iban-modal"
      >
        <Modal.Body className="apply-for-iban-modal__body">
          <button
            type="button"
            className="validation-popup__close"
            onClick={handleClose}
            aria-label="Close popup"
          >
            &times;
          </button>

          <div className="apply-for-iban-modal__header">
            <div className="apply-for-iban-modal__icon-shell" aria-hidden="true">
              <FontAwesomeIcon icon={faBuildingColumns} />
            </div>
            <h3>Link your BSL Account</h3>
            <p>
              To complete your IBAN request, please select the BSL branch that
              will handle any required documents or verification.
            </p>
          </div>

          <div className="apply-for-iban-modal__content">
            <FormSelect
              label="BSL Bank Branch"
              value={selectedBranchId}
              onChange={(event) => {
                setSelectedBranchId(event.target.value);
                if (error) setError("");
              }}
              options={branchOptions}
              error={error}
              disabled={loading || submitting}
            />

            {!loading && !branchOptions.slice(1).length ? (
              <p className="apply-for-iban-modal__hint">
                No branches are available right now.
              </p>
            ) : null}
          </div>

          <div className="apply-for-iban-modal__actions">
            <AppButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading || submitting}
            >
              Not Now
            </AppButton>
            <AppButton
              type="button"
              onClick={handleSubmit}
              disabled={loading || submitting || !branchOptions.slice(1).length}
              loading={submitting}
            >
              Submit
            </AppButton>
          </div>
        </Modal.Body>
      </Modal>

      <BlurLoading show={loading || submitting} />
    </>
  );
}
